/**
 * @fileoverview Getting an organization-portal link to the page it names.
 *
 * The program emails carry a button straight to a program page
 * (`backend/services/program_digest_service.py`). Two things stood between
 * that button and the page:
 *
 * 1. Signed out, the portal sent everyone to /org/login and then to /org, so
 *    the program was lost. The sign-in page now carries where you were going
 *    as `?next=` and returns you there.
 *    (A query, not `lib/returnTo`'s sessionStorage: the org portal signs in by
 *    password only, with no round trip off the site to survive, and the
 *    instructor portal's stored return must never land an org admin.)
 * 2. A program viewer and an administrator open the same program at different
 *    addresses (`/org/viewer/programs/:id` and `/org/programs/:id`), and each
 *    portal refuses the other's pages. A link that reaches the wrong one is
 *    sent to the right one rather than to a home page.
 */

import { isSafeReturn } from "@/lib/returnTo";

export const ORG_LOGIN_PATH = "/org/login";

/**
 * The sign-in address that brings you back to `location` afterwards. A bare
 * /org (or nothing) needs no return trip.
 *
 * @param {{pathname?: string, search?: string}} location
 */
export function orgLoginPath(location) {
  const here = `${location?.pathname || ""}${location?.search || ""}`;
  const next = safeOrgNext(here);
  return next && next !== "/org" ? `${ORG_LOGIN_PATH}?next=${encodeURIComponent(next)}` : ORG_LOGIN_PATH;
}

/**
 * `raw` if it is a page inside the organization portal, else null. Only a
 * same-site /org path is honoured, so a crafted `?next=` cannot send someone
 * off the site after they sign in (`//evil.example`, `https://…`), and the
 * sign-in pages themselves are never a destination.
 *
 * @param {string | null | undefined} raw
 * @returns {string | null}
 */
export function safeOrgNext(raw) {
  if (!isSafeReturn(raw) || raw.includes("\\")) return null;
  if (raw !== "/org" && !raw.startsWith("/org/") && !raw.startsWith("/org?")) return null;
  const path = raw.split(/[?#]/)[0];
  if (["/org/login", "/org/signup", "/org/forgot-password", "/org/verify-email"].includes(path)) return null;
  return raw;
}

const ADMIN_PROGRAM = /^\/org\/programs\/([^/?#]+)/;
const VIEWER_PROGRAM = /^\/org\/viewer\/programs\/([^/?#]+)/;

/**
 * Where a signed-in identity should be instead of `pathname`, or null when
 * the page is already theirs.
 *
 * - A viewer anywhere outside their own pages goes to their own page for the
 *   same program when the address names one, else to their home.
 * - An administrator at a viewer's program page goes to the admin's.
 *
 * @param {"admin" | "viewer" | string | undefined} role
 * @param {string} pathname
 */
export function portalRedirect(role, pathname) {
  if (role === "viewer") {
    if (pathname.startsWith("/org/viewer")) return null;
    const program = pathname.match(ADMIN_PROGRAM);
    return program ? `/org/viewer/programs/${program[1]}` : "/org/viewer";
  }
  if (role === "admin") {
    const program = pathname.match(VIEWER_PROGRAM);
    return program ? `/org/programs/${program[1]}` : null;
  }
  return null;
}
